import { useCallback, useEffect, useRef, useState } from "react";
import { Scanner } from "@yudiel/react-qr-scanner";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { QRScannerViewStyled } from "./styled";
import { Icons } from "components";
import { decodeQrFromImageFile } from "utils/functions/scanQR";
import {
  buildScannerConstraints,
  enableContinuousFocus,
  forgetPreferredCamera,
  hasTorch,
  rememberBestCamera,
  resolvePreferredCamera,
  setTorch,
  triggerRefocus,
} from "utils/functions/camera";

// วาด overlay เอง เลยปิด UI ที่ lib มีให้ทั้งหมด
const SCANNER_COMPONENTS = {
  finder: false,
  torch: false,
  zoom: false,
  onOff: false,
};

const SCANNER_STYLES = {
  container: { width: "100%", height: "100%", aspectRatio: "auto" },
};

/** สแกนไม่ติดภายในเวลานี้ → ชวนไปเลือกรูปแทน ซึ่งใช้กล้องเนทีฟที่โฟกัสได้จริง */
const PHOTO_HINT_MS = 8000;

/**
 * กล้องสแกน QR แบบเต็มจอ สไตล์แอปธนาคาร
 *
 * กฎเหล็ก: เปิดกล้อง (getUserMedia) ครั้งเดียวตลอดอายุ component
 * เพราะ LINE WebView และ iOS ขอ permission ใหม่ทุกครั้งที่เปิดกล้อง
 * ห้ามใส่ key ให้ <Scanner>, ห้ามสลับกล้องกลางคัน, ห้ามเปิดกล้องสำรวจก่อน
 *
 * เรื่องเลือกเลนส์อยู่ที่ utils/functions/camera.js — ถ้าอ่าน label ได้ก่อนเปิดกล้อง
 * ก็เลือกเลย ถ้าไม่ได้ก็ใช้ facingMode แล้วจำไว้ใช้ครั้งหน้า
 * ถ้าเบลอจริง ๆ ตาข่ายคือปุ่มคลังภาพ ไม่ใช่การสลับเลนส์
 */
const QRScannerView = ({
  onScan,
  onError,
  onClose,
  title,
  enableGallery = true,
}) => {
  const { t } = useTranslation(["common"]);

  const scannerRef = useRef(null);
  const galleryInputRef = useRef(null);
  const focusRingId = useRef(0);

  const [_isDecoding, _setIsDecoding] = useState(false);
  // undefined = ยังไม่รู้, null = ใช้ facingMode, string = ล็อกเลนส์ตัวนี้
  const [_deviceId, _setDeviceId] = useState(undefined);
  const [_hasCameraError, _setHasCameraError] = useState(false);
  const [_hasTorch, _setHasTorch] = useState(false);
  const [_isTorchOn, _setIsTorchOn] = useState(false);
  const [_showPhotoHint, _setShowPhotoHint] = useState(false);
  const [_focusRing, _setFocusRing] = useState(null);

  const hasRetried = useRef(false);
  const isCameraResolved = _deviceId !== undefined;

  const _getTrack = useCallback(
    () => scannerRef.current?.getStream()?.getVideoTracks?.()[0] || null,
    [],
  );

  // เลือกเลนส์ "ก่อน" เปิดกล้อง — ขั้นนี้ไม่แตะ getUserMedia จึงไม่มี popup
  useEffect(() => {
    let isMounted = true;
    resolvePreferredCamera()
      .then((id) => isMounted && _setDeviceId(id))
      .catch(() => isMounted && _setDeviceId(null));
    return () => {
      isMounted = false;
    };
  }, []);

  // สตรีมเริ่มไม่พร้อมกับ mount → รอ track ให้ live ก่อนค่อยตั้งค่ากล้อง
  useEffect(() => {
    if (!isCameraResolved) return;

    let isMounted = true;
    let attempt = 0;
    const timer = setInterval(async () => {
      attempt += 1;
      const track = _getTrack();
      if (track?.readyState === "live") {
        clearInterval(timer);
        if (!isMounted) return;
        await enableContinuousFocus(track);
        _setHasTorch(hasTorch(track));
        // ตอนนี้ label มองเห็นได้แล้ว เก็บไว้ใช้ครั้งหน้า — ไม่สลับกล้องในรอบนี้
        await rememberBestCamera();
      } else if (attempt > 25) {
        clearInterval(timer);
      }
    }, 200);

    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, [isCameraResolved, _getTrack]);

  // สแกนไม่ติดสักที → ชวนไปเลือกรูปแทน (ไม่สลับกล้องให้ เพราะนั่น = popup เพิ่ม)
  useEffect(() => {
    if (!isCameraResolved) return;

    const timer = setTimeout(() => _setShowPhotoHint(true), PHOTO_HINT_MS);
    return () => clearTimeout(timer);
  }, [isCameraResolved]);

  // เปิดด้วย deviceId ที่จำไว้แล้วพัง (เช่น id หมดอายุ) → ถอยไป facingMode ครั้งเดียว
  const _handleCameraError = useCallback(
    (error) => {
      if (_deviceId && !hasRetried.current) {
        hasRetried.current = true;
        forgetPreferredCamera();
        _setDeviceId(null);
        return;
      }
      // เปิดกล้องไม่ได้จริง ๆ (บาง in-app browser ไม่ให้ใช้ camera API)
      // ไม่ปล่อยให้เป็นจอดำ — บอกผู้ใช้แล้วชี้ไปที่ปุ่มคลังภาพที่ยังใช้ได้อยู่
      _setHasCameraError(true);
      onError && onError(error);
    },
    [_deviceId, onError],
  );

  const _handleScan = useCallback(
    (detectedCodes) => {
      if (!detectedCodes?.length) return;
      navigator.vibrate?.(60);
      onScan && onScan(detectedCodes);
    },
    [onScan],
  );

  const _handleTapFocus = useCallback(
    (e) => {
      const track = _getTrack();
      if (track) triggerRefocus(track);

      const rect = e.currentTarget.getBoundingClientRect();
      focusRingId.current += 1;
      _setFocusRing({
        id: focusRingId.current,
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    },
    [_getTrack],
  );

  const _handleToggleTorch = useCallback(
    async (e) => {
      e.stopPropagation();
      const track = _getTrack();
      if (!track) return;
      const next = !_isTorchOn;
      if (await setTorch(track, next)) _setIsTorchOn(next);
    },
    [_getTrack, _isTorchOn],
  );

  const _handleOpenGallery = useCallback((e) => {
    e.stopPropagation();
    galleryInputRef.current?.click();
  }, []);

  const _handlePickImage = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;

      _setIsDecoding(true);
      const value = await decodeQrFromImageFile(file);
      _setIsDecoding(false);

      if (!value) {
        toast.error(t("Invalid QR code", { ns: "common" }));
        return;
      }
      navigator.vibrate?.(60);
      onScan && onScan([{ rawValue: value }]);
    },
    [onScan, t],
  );

  return (
    <QRScannerViewStyled onClick={_handleTapFocus}>
      {isCameraResolved ? (
        <Scanner
          ref={scannerRef}
          styles={SCANNER_STYLES}
          components={SCANNER_COMPONENTS}
          constraints={buildScannerConstraints(_deviceId)}
          onError={_handleCameraError}
          onScan={_handleScan}
        />
      ) : (
        <div className="qsv_loading">
          {t("Opening camera", { ns: "common" })}
        </div>
      )}

      <div className="qsv_mask">
        <div className="qsv_frame">
          <span className="qsv_corner tl" />
          <span className="qsv_corner tr" />
          <span className="qsv_corner bl" />
          <span className="qsv_corner br" />
          <span className="qsv_line" />
        </div>
      </div>

      <div className="qsv_topbar">
        {onClose && (
          <button
            type="button"
            className="qsv_close"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <Icons.Close width={16} height={16} color="#FFFFFF" />
          </button>
        )}
        <div className="qsv_title">
          {title || t("Scan QR Code", { ns: "common" })}
        </div>
      </div>

      <div className="qsv_hint">
        {_hasCameraError
          ? t("Unable to open the camera", { ns: "common" })
          : t("Place the QR code inside the frame", { ns: "common" })}
        <div className="qsv_sub_hint">
          {_hasCameraError
            ? t("Choose a photo of the QR code instead", { ns: "common" })
            : t("Tap the screen to focus", { ns: "common" })}
        </div>
      </div>

      {_showPhotoHint && !_hasCameraError && enableGallery && (
        <button type="button" className="qsv_retry" onClick={_handleOpenGallery}>
          {t("Still blurry? Tap to use a photo of the QR code", {
            ns: "common",
          })}
        </button>
      )}

      <div className="qsv_actions">
        {_hasTorch && (
          <button
            type="button"
            className={`qsv_action${_isTorchOn ? " active" : ""}`}
            onClick={_handleToggleTorch}
          >
            <span className="qsv_action_icon">
              <Icons.Flash
                color={_isTorchOn ? "#4B1E78" : "#FFFFFF"}
                isActive={_isTorchOn}
              />
            </span>
            {t("Flash", { ns: "common" })}
          </button>
        )}
        {enableGallery && (
          <button
            type="button"
            className="qsv_action"
            onClick={_handleOpenGallery}
          >
            <span className="qsv_action_icon">
              <Icons.Gallery />
            </span>
            {t("Gallery", { ns: "common" })}
          </button>
        )}
      </div>

      {_focusRing && (
        <span
          key={_focusRing.id}
          className="qsv_focus_ring"
          style={{ left: _focusRing.x, top: _focusRing.y }}
          onAnimationEnd={() => _setFocusRing(null)}
        />
      )}

      {_isDecoding && (
        <div className="qsv_decoding">
          {t("Reading QR code", { ns: "common" })}
        </div>
      )}

      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        onChange={_handlePickImage}
        style={{ display: "none" }}
      />
    </QRScannerViewStyled>
  );
};

export default QRScannerView;
